// @owners { team: patients-team }
import { ActionSheetContext, Button, Column } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { useNavigation } from '@alto/navigation';
import {
  type MedDetailsPrescriptionCTA,
  type MedDetailsPrescriptionCTAParams,
  type MedDetailsPrescriptionCTAs,
} from '@alto/scriptdash/alto/medications/patients/v1/types';
import React, { useCallback, useContext, useState } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { prescriptionToItemKey } from '~shared/features/checkout/helpers';
import { PRESCRIPTION_PRICING_LABELS } from '~shared/features/pricing/constants';
import getPrescriptionPricingByID from '~shared/features/pricing/selectors/getPrescriptionPricingByID';
import { getItemPricingTotal } from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { usePrescription } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { PrescriptionStatusTracker } from '../../../alto-assistant';
import { OrderTypeActionSheet } from '../../../auto-refill';
import { useRemoveFromCart } from '../../../cart';
import { RenewalCancelActionSheet, RenewalRequestStatusActionSheet } from '../../../med-list';
import { RenewalRequestActionSheet } from '../../../my-meds';
import { OrderEarlyCTA } from '../../../progyny-order-early';
import { DiscontinueActionSheet } from '../DiscontinueActionSheet';
import { TurnOffAutoRefillActionSheet } from '../TurnOffAutoRefillActionSheet';

type Props = {
  readonly primaryPrescriptionId: number;
  readonly prescriptionId: number;
  readonly ctas: MedDetailsPrescriptionCTAs;
  readonly handleAddToCart: (prescriptionID: number) => Promise<void>;
  readonly medicationName: string;
};

const showCTA = (cta: MedDetailsPrescriptionCTA, inCart: boolean) => {
  return (
    cta?.type &&
    cta.type !== 'NONE' &&
    !(cta.type === 'ADD_TO_CART' && inCart) &&
    !(cta.type === 'REMOVE_FROM_CART' && !inCart)
  );
};

export const CTAs = ({ prescriptionId, ctas, handleAddToCart, medicationName, primaryPrescriptionId }: Props) => {
  const isWeb = getPlatformOS() === 'web';
  const { navigate } = useNavigation();
  const { trackEvent } = useAnalytics();
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const { isPrescriptionInCart } = usePrescriptionsInCart();
  const { handleRemoveFromCart } = useRemoveFromCart();

  const { prescription } = usePrescription({ prescriptionID: prescriptionId, enabled: true });
  const prescriptionPricing = useSelectorShared((state) => getPrescriptionPricingByID(state, prescriptionId));
  const { value: isProgynyWebEnabled } = Experimentation.useFeatureFlag('progyny_web');
  const isProgyny = getItemPricingTotal(prescriptionPricing).reason === PRESCRIPTION_PRICING_LABELS.PROGYNY;
  const isProgynyNativeOnly = isWeb && isProgyny && !isProgynyWebEnabled;
  const inCart = prescriptionId ? isPrescriptionInCart(prescriptionId) : false;
  const { primary, secondary, tertiary } = ctas;

  const [isLoading, setIsLoading] = useState(false);

  const showAddToCartActionSheet = useCallback(async () => {
    if (!prescriptionId) return;
    trackEvent({
      event: EVENTS.ADD_TO_CART_TAPPED,
      additionalFields: { prescriptionId },
      params: { origin: ORIGIN_NAMES.MED_DETAILS },
    });
    setIsLoading(true);
    try {
      await handleAddToCart(prescriptionId);
    } finally {
      setIsLoading(false);
    }
  }, [handleAddToCart, prescriptionId, trackEvent]);

  const handleViewOrder = useCallback(
    (params: MedDetailsPrescriptionCTAParams | null | undefined) => {
      if (params?.shipment_id) {
        navigate('RouteOrderDetails', {
          shipmentID: params.shipment_id,
        });
      }
    },
    [navigate],
  );

  const handleViewRefillRequest = useCallback(() => {
    if (!prescription) return;
    setActiveActionSheet(<RenewalRequestStatusActionSheet prescription={prescription} />);
  }, [setActiveActionSheet, prescription]);

  const handleRequestMoreRefills = useCallback(() => {
    setActiveActionSheet(<RenewalRequestActionSheet prescriptionIDs={[prescriptionId]} />);
  }, [prescriptionId, setActiveActionSheet]);

  const handleCheckStatus = useCallback(() => {
    setActiveActionSheet(
      <PrescriptionStatusTracker
        origin="MedDetails"
        prescriptionId={prescriptionId}
        prescriptionName={prescription?.medication_name || 'Your medication'}
        prescriptionStatus={prescription?.status}
      />,
    );
  }, [prescription?.medication_name, prescription?.status, prescriptionId, setActiveActionSheet]);

  const handlePrimaryPress = useCallback(() => {
    switch (primary?.type) {
      case 'VIEW_ORDER':
        handleViewOrder(primary?.params);
        return;
      case 'REQUEST_MORE_REFILLS':
        handleRequestMoreRefills();
        return;
      case 'VIEW_REFILL_REQUEST':
        handleViewRefillRequest();
        return;
      case 'CHECK_STATUS':
        handleCheckStatus();
        return;
      case 'ADD_TO_CART':
        void showAddToCartActionSheet();
        return;
      case 'NONE':
      default:
        return;
    }
  }, [
    primary,
    handleViewOrder,
    handleRequestMoreRefills,
    handleViewRefillRequest,
    handleCheckStatus,
    showAddToCartActionSheet,
  ]);

  const handleCancelRefillRequest = useCallback(() => {
    if (prescription) {
      setActiveActionSheet(<RenewalCancelActionSheet prescription={prescription} />);
    }
  }, [setActiveActionSheet, prescription]);

  const handleOrderEarly = useCallback(() => {
    navigate('RouteOrderEarlyForm', { prescriptionId });
  }, [navigate, prescriptionId]);

  const handleSecondaryPress = useCallback(() => {
    switch (secondary?.type) {
      case 'CANCEL_REFILL_REQUEST':
        handleCancelRefillRequest();
        return;
      case 'REMOVE_FROM_CART':
        handleRemoveFromCart({ item: prescriptionToItemKey(prescriptionId) });
        return;
      case 'PROGYNY_ORDER_EARLY':
        handleOrderEarly();
        return;
      case 'NONE':
      default:
        return;
    }
  }, [secondary?.type, handleCancelRefillRequest, handleRemoveFromCart, handleOrderEarly, prescriptionId]);

  const handleEnrollInAutoRefill = useCallback(() => {
    setActiveActionSheet(
      <OrderTypeActionSheet
        handleBack={closeActionSheet}
        initialAutoRefillEnabledValue
        prescriptionID={prescriptionId}
        orderTypeContext="Post checkout"
      />,
    );
  }, [closeActionSheet, prescriptionId, setActiveActionSheet]);

  const handleTurnOffAutoRefill = useCallback(() => {
    setActiveActionSheet(<TurnOffAutoRefillActionSheet prescriptionID={prescriptionId} />);
  }, [prescriptionId, setActiveActionSheet]);

  const handleDiscontinuePrescription = useCallback(() => {
    setActiveActionSheet(
      <DiscontinueActionSheet
        medicationName={medicationName}
        prescriptionId={prescriptionId}
        primaryPrescriptionId={primaryPrescriptionId}
      />,
    );
  }, [medicationName, prescriptionId, primaryPrescriptionId, setActiveActionSheet]);

  const handleTertiaryPress = useCallback(() => {
    switch (tertiary?.type) {
      case 'ENROLL_IN_AUTO_REFILL':
        handleEnrollInAutoRefill();
        return;
      case 'TURN_OFF_AUTO_REFILL':
        handleTurnOffAutoRefill();
        return;
      case 'DISCONTINUE':
        handleDiscontinuePrescription();
        return;
      case 'NONE':
      default:
        return;
    }
  }, [tertiary?.type, handleEnrollInAutoRefill, handleTurnOffAutoRefill, handleDiscontinuePrescription]);

  const isProgynyOrderEarlyOnWeb = isWeb && secondary?.type === 'PROGYNY_ORDER_EARLY';
  const showOrderEarlyCTA = isProgynyOrderEarlyOnWeb && isProgynyWebEnabled;
  const isOrderEarlyNativeOnly = isProgynyOrderEarlyOnWeb && !isProgynyWebEnabled;

  const primaryButtonLabel = isProgynyNativeOnly ? 'Order on the Alto app' : primary?.copy;
  const secondaryButtonLabel = isOrderEarlyNativeOnly ? 'Order early on the Alto app' : secondary?.copy;
  const secondaryContent =
    showOrderEarlyCTA && prescription ? (
      <OrderEarlyCTA
        prescription={prescription}
        showTag={false}
        small={false}
      />
    ) : (
      <Button
        disabled={isLoading || isOrderEarlyNativeOnly}
        onPress={handleSecondaryPress}
        type="secondary"
      >
        {secondaryButtonLabel}
      </Button>
    );

  return (
    <Column gap="XS">
      {ctas?.primary && showCTA(ctas.primary, inCart) ? (
        <Button
          disabled={isLoading || isProgynyNativeOnly}
          onPress={handlePrimaryPress}
        >
          {primaryButtonLabel}
        </Button>
      ) : null}
      {ctas?.secondary && showCTA(ctas.secondary, inCart) ? secondaryContent : null}
      {ctas?.tertiary && showCTA(ctas.tertiary, inCart) ? (
        <Button
          onPress={handleTertiaryPress}
          type="tertiary"
        >
          {tertiary?.copy}
        </Button>
      ) : null}
    </Column>
  );
};
